import { useEffect, useRef, type RefObject } from "react";

export function useCalendarPinch(
  root: RefObject<HTMLDivElement | null>,
  onZoom?: (direction: 1 | -1, anchor?: Date) => void,
) {
  const callback = useRef(onZoom);
  callback.current = onZoom;
  useEffect(() => {
    const node = root.current;
    if (!node || !callback.current) return;
    let gesture: {
      distance: number;
      ratio: number;
      ids: number[];
      anchor?: Date;
    } | null = null;
    const distance = (touches: TouchList) =>
      Math.hypot(
        touches[0].clientX - touches[1].clientX,
        touches[0].clientY - touches[1].clientY,
      );
    const start = (event: TouchEvent) => {
      if (event.touches.length !== 2) {
        if (event.touches.length > 2) gesture = null;
        return;
      }
      event.preventDefault();
      const x = (event.touches[0].clientX + event.touches[1].clientX) / 2;
      const y = (event.touches[0].clientY + event.touches[1].clientY) / 2;
      const hit = document.elementFromPoint(x, y);
      const dateKey =
        hit?.closest<HTMLElement>("[data-calendar-date]")?.dataset
          .calendarDate ??
        hit?.closest<HTMLElement>("[data-period-key]")?.dataset.periodKey;
      // Parse partial period keys explicitly in local time (no UTC date shift).
      const parts = dateKey?.split("-").map(Number);
      gesture = {
        distance: Math.max(30, distance(event.touches)),
        ratio: 1,
        ids: [event.touches[0].identifier, event.touches[1].identifier],
        anchor: parts
          ? new Date(parts[0], (parts[1] ?? 1) - 1, parts[2] ?? 1, 12)
          : undefined,
      };
    };
    const move = (event: TouchEvent) => {
      if (!gesture) return;
      if (event.touches.length >= 2) event.preventDefault();
      if (
        event.touches.length !== 2 ||
        !gesture.ids.every((id) =>
          Array.from(event.touches).some((t) => t.identifier === id),
        )
      )
        return;
      gesture.ratio = distance(event.touches) / gesture.distance;
    };
    const end = (event: TouchEvent) => {
      if (!gesture || event.touches.length) return;
      const saved = gesture;
      gesture = null;
      if (saved.ratio >= 1.22) callback.current?.(1, saved.anchor);
      else if (saved.ratio <= 0.8) callback.current?.(-1, saved.anchor);
    };
    const cancel = () => {
      gesture = null;
    };
    const nativeGesture = (event: Event) => {
      if (gesture) event.preventDefault();
    };
    node.addEventListener("touchstart", start, { passive: false });
    node.addEventListener("touchmove", move, { passive: false });
    node.addEventListener("touchend", end);
    node.addEventListener("touchcancel", cancel);
    node.addEventListener("gesturestart", nativeGesture, { passive: false });
    node.addEventListener("gesturechange", nativeGesture, { passive: false });
    return () => {
      node.removeEventListener("touchstart", start);
      node.removeEventListener("touchmove", move);
      node.removeEventListener("touchend", end);
      node.removeEventListener("touchcancel", cancel);
      node.removeEventListener("gesturestart", nativeGesture);
      node.removeEventListener("gesturechange", nativeGesture);
    };
  }, [root]);
}
